/**
 * OptionRadioGroup — generic, typed radio group inside a fieldset legend.
 *
 * A standard pattern for in-page demos: "pick one of N labelled choices
 * and show the selection downstream." This component renders a fieldset
 * with a legend, a column of radio inputs, and accent-highlights the
 * currently selected row.
 *
 * The component is parameterised over the option's value type so callsites
 * stay typed end-to-end (no string-typing of enum values).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId } from "react";
import { GRAY } from "../design-system/tokens.js";

export interface OptionRadioChoice<V extends string> {
  /** The value carried by this choice. */
  readonly value: V;
  /** Human-readable label shown next to the radio input. */
  readonly label: string;
}

export interface OptionRadioGroupProps<V extends string> {
  /** Legend rendered at the top-left of the fieldset. */
  readonly legend: string;
  /** Available choices; the first match for `value` is the selected one. */
  readonly choices: readonly OptionRadioChoice<V>[];
  /** Currently selected value. */
  readonly value: V;
  /** Called with the newly selected value when the user picks a row. */
  readonly onChange: (value: V) => void;
  /** Optional accent color used to highlight the selected row. */
  readonly accent?: string;
  /**
   * Optional explicit `name` for the radio group (used to scope the
   * native radio behavior). Defaults to a stable React-generated id.
   */
  readonly name?: string;
}

/**
 * Render a labelled, typed radio group.
 *
 * Generic over the value type `V` so callers cannot accidentally pass an
 * unrecognised string. The selected row is highlighted with the accent
 * color; non-selected rows render in the default body color.
 *
 * Migration: new call-sites should prefer the Radix-backed
 * `RadioGroup` primitive (`./primitives/RadioGroup.tsx`), which ships
 * full WAI-ARIA semantics and keyboard navigation. This component
 * remains for the inline-fieldset surface (native `<fieldset>` /
 * `<legend>` with a typed value generic) that the Radix wrapper does
 * not expose directly. Tracked by issue #1577.
 */
export function OptionRadioGroup<V extends string>({
  legend,
  choices,
  value,
  onChange,
  accent,
  name,
}: OptionRadioGroupProps<V>): React.JSX.Element {
  const fallbackName = useId();
  const groupName = name ?? fallbackName;
  return (
    <fieldset
      style={{
        border: `1px solid ${GRAY[300]}`,
        borderRadius: "0.375rem",
        padding: "0.5rem",
        margin: 0,
        background: "white",
      }}
    >
      <legend style={{ fontSize: "0.6875rem", color: GRAY[600], padding: "0 0.25rem" }}>
        {legend}
      </legend>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
        {choices.map((choice) => {
          const selected = choice.value === value;
          return (
            <label
              key={choice.value}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                fontSize: "0.8125rem",
                cursor: "pointer",
                color: selected ? (accent ?? GRAY[900]) : GRAY[700],
                fontWeight: selected ? 600 : 400,
              }}
            >
              <input
                type="radio"
                name={groupName}
                value={choice.value}
                checked={selected}
                onChange={() => {
                  onChange(choice.value);
                }}
              />
              <span>{choice.label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
